import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Switch,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname } from "../utils";

interface MCPServer {
  id: string;
  name: string;
  serverType: string;
  enabled: boolean;
  status?: string;
}

interface MCPServerSelectProps {
  getToken: Function;
  eventListener: Function;
  companyId: string;
  value: string[]; // Array of selected server IDs
  onValueChange: (value: string[]) => void;
  title?: string;
  sx?: any;
}

const MCPServerSelect: React.FC<MCPServerSelectProps> = ({
  getToken,
  eventListener,
  companyId,
  value = [],
  onValueChange,
  title = "MCP Servers (Optional)",
  sx = {},
}) => {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId) {
      loadMCPServers();
    }
  }, [companyId]);

  // Filter out invalid server IDs when servers list changes
  useEffect(() => {
    if (servers.length > 0 && value.length > 0) {
      const availableServerIds = servers.map(server => server.id);
      const validSelectedIds = value.filter(id => availableServerIds.includes(id));
      
      // If some selected servers are no longer available, update the selection
      if (validSelectedIds.length !== value.length) {
        console.log('Filtering out unavailable MCP servers:', value.filter(id => !availableServerIds.includes(id)));
        onValueChange(validSelectedIds);
      }
    }
  }, [servers, value, onValueChange]);

  const loadMCPServers = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/integrations/mcp/list/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setServers(response.data.servers || []);
    } catch (error) {
      console.error("Failed to load MCP servers:", error);
      eventListener({
        type: "SET_ERROR",
        error: "Failed to load MCP servers",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (serverId: string) => {
    const currentIndex = value.indexOf(serverId);
    const newValue = [...value];

    if (currentIndex === -1) {
      newValue.push(serverId);
    } else {
      newValue.splice(currentIndex, 1);
    }

    onValueChange(newValue);
  };

  return (
    <FormControl component="fieldset" sx={{ width: "100%", ...sx }}>
      <FormLabel component="legend">{title}</FormLabel>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Connect MCP servers to provide additional tools and capabilities to this
        agent
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : servers.length === 0 ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          No MCP servers configured. Configure them in the Integrations menu.
        </Alert>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {servers.map((server) => (
            <FormControlLabel
              key={server.id}
              control={
                <Switch
                  checked={value.includes(server.id)}
                  onChange={() => handleToggle(server.id)}
                  disabled={!server.enabled}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2">{server.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({server.serverType})
                  </Typography>
                  {!server.enabled && (
                    <Typography variant="caption" color="error">
                      (Disabled)
                    </Typography>
                  )}
                  {server.status === "error" && (
                    <Typography variant="caption" color="error">
                      (Error)
                    </Typography>
                  )}
                </Box>
              }
            />
          ))}
        </Box>
      )}
    </FormControl>
  );
};

export default MCPServerSelect;
